import type { ComponentPropsWithRef, HTMLAttributes, ReactNode, Ref, TdHTMLAttributes, ThHTMLAttributes } from "react";
import { createContext, isValidElement, useContext } from "react";
import { ArrowDown, ChevronsUpDown, Copy, Edit, HelpCircle, Trash2, MoreHorizontal } from "lucide-react";
import type {
    CellProps as AriaCellProps,
    ColumnProps as AriaColumnProps,
    RowProps as AriaRowProps,
    TableHeaderProps as AriaTableHeaderProps,
    TableProps as AriaTableProps,
} from "react-aria-components";
import {
    Cell as AriaCell,
    Collection as AriaCollection,
    Column as AriaColumn,
    Group as AriaGroup,
    Row as AriaRow,
    Table as AriaTable,
    TableBody as AriaTableBody,
    TableHeader as AriaTableHeader,
    useTableOptions,
} from "react-aria-components";
import { Badge } from "@/src/components/base/badges/badges";
import { Checkbox as AriaCheckbox } from "react-aria-components";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cx } from "@/utils/cx";

export const TableRowActionsDropdown = () => (
    <DropdownMenu>
        <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
            </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-min">
            <DropdownMenuItem>
                <Edit className="h-4 w-4 mr-2" />
                <span className="pr-4">Edit</span>
            </DropdownMenuItem>
            <DropdownMenuItem>
                <Copy className="h-4 w-4 mr-2" />
                <span className="pr-4">Copy link</span>
            </DropdownMenuItem>
            <DropdownMenuItem>
                <Trash2 className="h-4 w-4 mr-2" />
                <span className="pr-4">Delete</span>
            </DropdownMenuItem>
        </DropdownMenuContent>
    </DropdownMenu>
);

const TableContext = createContext<{ size: "sm" | "md" }>({ size: "md" });

const TableCardRoot = ({ children, className, size = "md", ...props }: HTMLAttributes<HTMLDivElement> & { size?: "sm" | "md" }) => {
    return (
        <TableContext.Provider value={{ size }}>
            <div {...props} className={cx("overflow-hidden rounded-xl bg-white shadow-xs ring-1 ring-gray-200", className)}>
                {children}
            </div>
        </TableContext.Provider>
    );
};

interface TableCardHeaderProps {
    title: string;
    badge?: ReactNode;
    description?: string;
    contentTrailing?: ReactNode;
    className?: string;
}

const TableCardHeader = ({ title, badge, description, contentTrailing, className }: TableCardHeaderProps) => {
    const { size } = useContext(TableContext);

    return (
        <div
            className={cx(
                "relative flex flex-col items-start gap-4 border-b border-gray-200 bg-white px-4 md:flex-row",
                size === "sm" ? "py-4 md:px-5" : "py-5 md:px-6",
                className,
            )}
        >
            <div className="flex flex-1 flex-col gap-0.5">
                <div className="flex items-center gap-2">
                    <h2 className={cx("font-semibold text-gray-900", size === "sm" ? "text-md" : "text-lg")}>{title}</h2>
                    {badge ? (
                        isValidElement(badge) ? (
                            badge
                        ) : (
                            <Badge variant="secondary" className="text-xs">
                                {badge}
                            </Badge>
                        )
                    ) : null}
                </div>
                {description && <p className="text-sm text-gray-500">{description}</p>}
            </div>
            {contentTrailing}
        </div>
    );
};

interface TableRootProps extends AriaTableProps, Omit<ComponentPropsWithRef<"table">, "className" | "slot" | "style"> {
    size?: "sm" | "md";
}

const TableRoot = ({ className, size = "md", ...props }: TableRootProps) => {
    const context = useContext(TableContext);

    return (
        <TableContext.Provider value={{ size: context?.size ?? size }}>
            <div className="overflow-x-auto">
                <AriaTable className={(state) => cx("w-full table-fixed", typeof className === "function" ? className(state) : className)} {...props} />
            </div>
        </TableContext.Provider>
    );
};
TableRoot.displayName = "Table";

interface TableHeaderProps<T extends object>
    extends AriaTableHeaderProps<T>,
        Omit<ComponentPropsWithRef<"thead">, "children" | "className" | "slot" | "style"> {
    bordered?: boolean;
}

const TableHeader = <T extends object>({ columns, children, bordered = true, className, ...props }: TableHeaderProps<T>) => {
    const { size } = useContext(TableContext);
    const { selectionBehavior, selectionMode } = useTableOptions();

    return (
        <AriaTableHeader
            {...props}
            className={(state) =>
                cx(
                    "relative bg-gray-50",
                    size === "sm" ? "h-9" : "h-11",
                    bordered &&
                        "[&>tr>th]:after:pointer-events-none [&>tr>th]:after:absolute [&>tr>th]:after:inset-x-0 [&>tr>th]:after:bottom-0 [&>tr>th]:after:h-px [&>tr>th]:after:bg-gray-200 [&>tr>th]:focus-visible:after:bg-transparent",
                    typeof className === "function" ? className(state) : className,
                )
            }
        >
            {selectionBehavior === "toggle" && (
                <AriaColumn className={cx("relative py-2 pr-0 pl-4", size === "sm" ? "w-9 md:pl-5" : "w-11 md:pl-6")}>
                    {selectionMode === "multiple" && (
                        <AriaCheckbox slot="selection" className="flex items-center justify-center w-4 h-4">
                            {({ isSelected }) => (
                                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                    isSelected ? 'bg-blue-600 border-blue-600' : 'bg-white border-gray-300'
                                }`}>
                                    {isSelected && (
                                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                </div>
                            )}
                        </AriaCheckbox>
                    )}
                </AriaColumn>
            )}
            <AriaCollection items={columns}>{children}</AriaCollection>
        </AriaTableHeader>
    );
};

interface TableHeadProps<T extends object> extends AriaColumnProps, Omit<ThHTMLAttributes<HTMLTableCellElement>, "children" | "className"> {
    tooltip?: string;
    label?: string;
}

const TableHead = <T extends object>({ children, label, tooltip, className, ...props }: TableHeadProps<T>) => {
    const { size } = useContext(TableContext);

    const content = (
        <AriaColumn
            {...props}
            className={(state) =>
                cx(
                    "relative px-6 py-3 text-left text-xs font-medium uppercase tracking-wider",
                    state.isSorted && "text-gray-900",
                    !state.isSorted && "text-gray-500",
                    state.allowsSorting && "cursor-pointer select-none hover:text-gray-700",
                    size === "sm" && "py-2",
                    typeof className === "function" ? className(state) : className,
                )
            }
        >
            {({ allowsSorting, sortDirection }) => (
                <div className="flex items-center gap-1">
                    <span>{label || children}</span>
                    {allowsSorting && (
                        <span className="ml-1">
                            {sortDirection === "ascending" ? (
                                <ArrowDown className="h-3 w-3 rotate-180" />
                            ) : sortDirection === "descending" ? (
                                <ArrowDown className="h-3 w-3" />
                            ) : (
                                <ChevronsUpDown className="h-3 w-3 opacity-50" />
                            )}
                        </span>
                    )}
                    {tooltip && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <HelpCircle className="h-3 w-3 text-gray-400" />
                                </TooltipTrigger>
                                <TooltipContent>{tooltip}</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                </div>
            )}
        </AriaColumn>
    );

    return content;
};

interface TableBodyProps<T extends object> {
    items: T[];
    children: (item: T) => ReactNode;
    className?: string;
}

const TableBody = <T extends object>({ items, children, className }: TableBodyProps<T>) => {
    return (
        <AriaTableBody items={items} className={cx("divide-y divide-gray-200 bg-white", className)}>
            {children}
        </AriaTableBody>
    );
};

interface TableRowProps<T extends object> extends AriaRowProps<T>, Omit<ComponentPropsWithRef<"tr">, "children" | "className" | "id"> {
    children: ReactNode;
}

const TableRow = <T extends object>({ children, className, ...props }: TableRowProps<T>) => {
    const { size } = useContext(TableContext);
    const { selectionBehavior } = useTableOptions();

    return (
        <AriaRow
            {...props}
            className={(state) =>
                cx(
                    "relative transition-colors hover:bg-gray-50",
                    state.isSelected && "bg-blue-50",
                    state.isDisabled && "opacity-50",
                    typeof className === "function" ? className(state) : className,
                )
            }
        >
            {selectionBehavior === "toggle" && (
                <AriaCell className={cx("relative py-4 pr-0 pl-4", size === "sm" ? "md:pl-5" : "md:pl-6")}>
                    <AriaCheckbox slot="selection" className="flex items-center justify-center w-4 h-4">
                        {({ isSelected }) => (
                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                                isSelected ? 'bg-blue-600 border-blue-600' : 'bg-white border-gray-300 hover:border-gray-400'
                            }`}>
                                {isSelected && (
                                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </div>
                        )}
                    </AriaCheckbox>
                </AriaCell>
            )}
            {children}
        </AriaRow>
    );
};

interface TableCellProps extends AriaCellProps, Omit<TdHTMLAttributes<HTMLTableCellElement>, "children" | "className"> {
    children: ReactNode;
}

const TableCell = ({ children, className, ...props }: TableCellProps) => {
    const { size } = useContext(TableContext);

    // Stop pointer events from propagating to the row to prevent row clicks from triggering selection
    // Only the checkbox cell (rendered separately in TableRow) should trigger selection
    const handlePointerDown = (e: React.PointerEvent) => e.stopPropagation();
    const handlePointerUp = (e: React.PointerEvent) => e.stopPropagation();

    return (
        <AriaCell
            {...props}
            className={(state) =>
                cx(
                    "relative px-6 py-4 text-sm",
                    size === "sm" && "py-3",
                    typeof className === "function" ? className(state) : className,
                )
            }
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
        >
            {children}
        </AriaCell>
    );
};

export const TableCard = {
    Root: TableCardRoot,
    Header: TableCardHeader,
};

export const Table = Object.assign(TableRoot, {
    Header: TableHeader,
    Head: TableHead,
    Body: TableBody,
    Row: TableRow,
    Cell: TableCell,
});